/**
 * TemplateSelector
 *
 * Searchable combobox for picking a template (a list with `werk_id IS NULL`).
 *
 * Used by both the checklist creation flow (Phase 5.7) and the EXPAND_PROTO
 * action in template editing (Phase 7.4) so a template can be expanded into
 * either a fresh checklist or another template.
 *
 * The component is intentionally framework-agnostic about how its value flows
 * (pass-through `value` / `onChange`); callers wire it into react-hook-form,
 * URL state, or local state as appropriate.
 */
import { useMemo, useState } from 'react';
import { Check, ChevronsUpDown, FileText } from 'lucide-react';
import { TagBadge } from '@/features/lists/components/ListTagsField';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { useQuery } from '@tanstack/react-query';
import { listQueries } from '@/features/lists/lists.queries';

/**
 * Fetch all templates (lists where `werk_id IS NULL`). Lives co-located with
 * TemplateSelector since this is the only consumer post-A4 boundary cleanup.
 */
function useTemplates() {
  return useQuery(listQueries.list({ werk_id: 'is.null' }));
}

export interface TemplateSelectorProps {
  /** Currently selected template id, or `null` for "no template / blank". */
  value: string | null;
  /** Called with the new template id, or `null` when cleared. */
  onChange: (templateId: string | null) => void;
  /** Trigger placeholder when no template is selected. */
  placeholder?: string;
  /** Disable interaction (still renders the trigger). */
  disabled?: boolean;
  /**
   * Label shown for the "no template" option. Defaults to a sensible string,
   * but callers (e.g. checklist creation) can override to "Blank checklist".
   */
  noneLabel?: string;
  /** Optional className applied to the trigger button. */
  className?: string;
  /**
   * Restrict the listed templates to those whose `tags` array contains every
   * entry in this list (e.g. `['label:composed', 'department:climate']`).
   * Empty/undefined → no filtering. Will be removed once label-based tagging
   * replaces the legacy `key:value` tag format.
   */
  prefilterTags?: string[];
}

export function TemplateSelector({
  value,
  onChange,
  placeholder = 'Select a template…',
  disabled = false,
  noneLabel = 'No template',
  className,
  prefilterTags,
}: TemplateSelectorProps) {
  const [open, setOpen] = useState(false);
  const [removedFilters, setRemovedFilters] = useState<string[]>([]);
  const { data: templates, isLoading } = useTemplates();

  const activeFilters = useMemo(
    () => (prefilterTags ?? []).filter((t) => !removedFilters.includes(t)),
    [prefilterTags, removedFilters],
  );

  const filteredTemplates = useMemo(() => {
    if (!templates || activeFilters.length === 0) {
      return templates;
    }
    return templates.filter((t) =>
      activeFilters.every((pt) => (t.tags ?? []).includes(pt)),
    );
  }, [templates, activeFilters]);

  // Selected template lookup uses the unfiltered list so a previously chosen
  // template still resolves even if it falls outside the current prefilter.
  const selected = useMemo(
    () => templates?.find((t) => t.id === value) ?? null,
    [templates, value],
  );

  const handleSelect = (templateId: string | null) => {
    onChange(templateId);
    setOpen(false);
  };

  if (isLoading && !templates) {
    return <Skeleton className={cn('h-8 w-full', className)} />;
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className={cn(
            'h-8 w-full justify-between px-2 text-sm font-normal',
            !selected && 'text-muted-foreground',
            className,
          )}
        >
          <span className="flex min-w-0 items-center gap-2 truncate">
            <FileText className="h-3.5 w-3.5 shrink-0 opacity-70" />
            <span className="truncate">
              {selected ? selected.name || 'Untitled template' : placeholder}
            </span>
          </span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0">
        {activeFilters.length > 0 && (
          <div className="flex flex-wrap items-center gap-1 border-b px-2 py-1.5">
            <span className="text-muted-foreground text-xs">Filters:</span>
            {activeFilters.map((tag) => (
              <TagBadge
                key={tag}
                tag={tag}
                onRemove={() => setRemovedFilters((prev) => [...prev, tag])}
              />
            ))}
          </div>
        )}
        <Command
          filter={(itemValue, search, keywords) => {
            if (!search) return 1;
            const haystack = [itemValue, ...(keywords ?? [])]
              .join(' ')
              .toLowerCase();
            return haystack.includes(search.toLowerCase()) ? 1 : 0;
          }}
        >
          <CommandInput placeholder="Search templates…" className="h-9" />
          <CommandList>
            <CommandEmpty>No templates found.</CommandEmpty>
            <CommandGroup>
              <CommandItem
                value="__none__"
                keywords={['none', 'blank', 'empty']}
                onSelect={() => handleSelect(null)}
              >
                <Check
                  className={cn(
                    'mr-2 h-4 w-4',
                    value === null ? 'opacity-100' : 'opacity-0',
                  )}
                />
                <span className="text-muted-foreground italic">
                  {noneLabel}
                </span>
              </CommandItem>
              {filteredTemplates?.map((template) => (
                <CommandItem
                  key={template.id}
                  value={template.id}
                  keywords={[template.name, ...(template.tags ?? [])].filter(
                    Boolean,
                  )}
                  onSelect={() => handleSelect(template.id)}
                >
                  <Check
                    className={cn(
                      'mr-2 h-4 w-4',
                      value === template.id ? 'opacity-100' : 'opacity-0',
                    )}
                  />
                  <span className="truncate">
                    {template.name || 'Untitled template'}
                  </span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
